import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Flyout } from '@els/els-react--flyout';
import { Modal } from '@els/els-react--modal';
import useBreakpoint from '@Hooks/useBreakpoint';

/**
 * Structure toolbar menus live inside a horizontally scrolling, paint-contained
 * reaction scheme. Use a page-level modal on mobile so their content is not
 * clipped by that container; retain the standard Leyden flyout on larger
 * viewports.
 */
const StructureToolbarFlyout = forwardRef(
  (
    {
      children,
      className,
      flyout,
      id,
      isControlled = false,
      isDisabled = false,
      isOpen = false,
      isReadOnly = false,
      messages,
      modalLabel,
      modalClassName,
      shouldCloseOnESC = false,
      onToggle = () => {},
      ...flyoutProps
    },
    ref
  ) => {
    const isDesktop = useBreakpoint('mobile');
    const desktopFlyoutRef = useRef(null);
    const triggerRef = useRef(null);
    const [internalOpen, setInternalOpen] = useState(false);
    const actualIsOpen = isControlled ? isOpen : internalOpen;

    const setVisibility = useCallback(
      (nextOpen) => {
        if (!isControlled) {
          setInternalOpen(nextOpen);
        }
        onToggle(nextOpen);
      },
      [isControlled, onToggle]
    );

    const closeModal = useCallback(() => {
      setVisibility(false);
      triggerRef.current?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')?.focus();
    }, [setVisibility]);

    const openMobileModal = useCallback(
      (nextOpen) => {
        if (nextOpen) {
          setVisibility(true);
        }
      },
      [setVisibility]
    );

    useImperativeHandle(
      ref,
      () => ({
        toggleVisibility(nextOpen) {
          if (isDesktop) {
            desktopFlyoutRef.current?.toggleVisibility(nextOpen);
          } else {
            setVisibility(nextOpen);
          }
        },
      }),
      [isDesktop, setVisibility]
    );

    if (isDesktop) {
      return (
        <Flyout
          ref={desktopFlyoutRef}
          className={className}
          flyout={flyout}
          id={id}
          isControlled={isControlled}
          isDisabled={isDisabled}
          isOpen={isOpen}
          isReadOnly={isReadOnly}
          messages={messages}
          shouldCloseOnESC={shouldCloseOnESC}
          onToggle={onToggle}
          {...flyoutProps}>
          {children}
        </Flyout>
      );
    }

    return (
      <>
        <div ref={triggerRef} data-testid="structure-toolbar-modal-trigger">
          <Flyout
            {...flyoutProps}
            className={className}
            flyout={null}
            id={id}
            isControlled
            isDisabled={isDisabled}
            isOpen={false}
            isReadOnly={isReadOnly}
            messages={messages}
            showCloseButton={false}
            onToggle={openMobileModal}>
            {children}
          </Flyout>
        </div>
        {actualIsOpen && (
          <Modal
            className={modalClassName}
            focusTrapProps={{ active: true }}
            fullPage="page"
            label={modalLabel}
            messages={messages}
            shouldCloseOnEscape={shouldCloseOnESC}
            onClose={closeModal}>
            <div>{flyout}</div>
          </Modal>
        )}
      </>
    );
  }
);

StructureToolbarFlyout.displayName = 'StructureToolbarFlyout';

StructureToolbarFlyout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  flyout: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  id: PropTypes.string,
  isControlled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isOpen: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  messages: PropTypes.object,
  modalLabel: PropTypes.string.isRequired,
  modalClassName: PropTypes.string,
  shouldCloseOnESC: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default StructureToolbarFlyout;
